import { t } from '@lingui/macro';
import { useCallback } from 'react';

import { fetchJson } from '@api/ApiHelper';
import { ToastLooks, useCreateToast } from '@components/common/toast';

import { amplitudeService } from '../../../../services/amplitude';

interface MergeIdentitiesParams {
  sourceIdentityId: string;
  destinationIdentityId: string;
  destinationName: string;
  sourceName: string;
  /** For People Hub MergeIdentities analytics (#7). */
  analyticsPeopleHub?: {
    destinationFaceCountBefore: number;
  };
}

interface MergeIdentitiesResponse {
  destination_identity_id?: string;
  source_identity_id?: string;
  faces_moved?: number;
  message?: string;
  id?: string;
  identity_name?: string;
  source_url?: string;
}

export const useMergeIdentities = (
  onSuccess?: () => void
): { mergeIdentities: (params: MergeIdentitiesParams) => Promise<{ success: boolean; facesMoved?: number }> } => {
  const { toast } = useCreateToast();

  const mergeIdentities = useCallback(
    async ({
      sourceIdentityId,
      destinationIdentityId,
      destinationName,
      sourceName,
      analyticsPeopleHub,
    }: MergeIdentitiesParams): Promise<{ success: boolean; facesMoved?: number }> => {
      try {
        // Get brandfolder key from global BFG object
        const brandfolderKey = BFG.brandfolder_key;

        if (!brandfolderKey) {
          toast(t`Brandfolder not found`, ToastLooks.Error);
          return { success: false };
        }

        if (!sourceIdentityId || !destinationIdentityId) {
          toast(t`Source and destination identity IDs are required`, ToastLooks.Error);
          return { success: false };
        }

        // Make API call to merge identities
        const response = await fetchJson<MergeIdentitiesResponse>({
          url: `/api/v4/private/people_tagging/brandfolders/${brandfolderKey}/identities/merge`,
          method: 'POST',
          body: {
            destination_identity_id: destinationIdentityId,
            source_identity_id: sourceIdentityId,
            destination_name: destinationName,
            source_name: sourceName,
          },
        });

        const facesMoved = response.faces_moved ?? 0;
        if (
          analyticsPeopleHub &&
          typeof window !== 'undefined' &&
          window.BFG?.hasFeature?.('people_tagging') &&
          amplitudeService.isReady()
        ) {
          const destBefore = analyticsPeopleHub.destinationFaceCountBefore;
          const eventArea = 'AppFrontend';
          const eventName = 'ButtonClick';
          const productArea = 'BulkManagement';
          const featureArea = 'PeopleHub';
          const elementName = 'MergeIdentities';
          const elementLocation = 'PeopleHubPage';
          const eventFull = `${eventArea}.${productArea}.${featureArea}.${elementName}.${eventName}`;
          amplitudeService.trackEvent(eventName, {
            eventArea,
            productArea,
            featureArea,
            elementLocation,
            elementName,
            eventName,
            eventFull,
            brandfolderId: window.BFG?.brandfolder_key ?? undefined,
            organizationId: window.BFG?.organization_key ?? undefined,
            userId: window.BFG?.currentUser?.user_id ?? undefined,
            userRole: window.BFG?.currentUser?.role ?? undefined,
            sourceIdentityCount: 1,
            totalFacesAfterMerge: destBefore + facesMoved,
          });
        }

        // Call success callback if provided (e.g., to refetch data)
        onSuccess?.();

        return { success: true, facesMoved };
      } catch (error) {
        toast(t`Failed to merge identities. Please try again.`, ToastLooks.Error);
        return { success: false };
      }
    },
    [onSuccess, toast]
  );

  return { mergeIdentities };
};
